import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { dealsService, messagesService, notificationsService } from '../../services/supabaseService';
import Header from '../../components/ui/Header';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import Icon from '../../components/AppIcon';
import ChatMessage from './components/ChatMessage';
import StatusTimeline from './components/StatusTimeline';
import DealSummary from './components/DealSummary';
import ChatInput from './components/ChatInput';
import ActionPanel from './components/ActionPanel';
import ProgressBar from '../../components/ui/ProgressBar';

const DealRoomPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const chatContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [showSummaryPanel, setShowSummaryPanel] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [typingUserId, setTypingUserId] = useState(null);
  const [deal, setDeal] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageChannel, setMessageChannel] = useState(null);
  const [dealChannel, setDealChannel] = useState(null);
  const [typingChannel, setTypingChannel] = useState(null);
  const [notificationChannel, setNotificationChannel] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/authentication-screen');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (chatContainerRef?.current) {
      chatContainerRef.current.scrollTop = chatContainerRef?.current?.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    const dealId = location?.state?.dealId;
    if (dealId && user?.id) {
      loadDealData(dealId);
    }
  }, [location?.state?.dealId, user]);

  useEffect(() => {
    return () => {
      if (messageChannel) {
        messagesService?.unsubscribe(messageChannel);
      }
      if (dealChannel) {
        dealsService?.unsubscribe(dealChannel);
      }
      if (typingChannel) {
        messagesService?.unsubscribe(typingChannel);
      }
      if (notificationChannel) {
        notificationsService?.unsubscribe(notificationChannel);
      }
      if (typingTimeoutRef?.current) {
        clearTimeout(typingTimeoutRef?.current);
      }
    };
  }, [messageChannel, dealChannel, typingChannel, notificationChannel]);

  const loadDealData = async (dealId) => {
    try {
      setLoading(true);
      const dealData = await dealsService?.getById(dealId);
      setDeal(dealData);

      const messagesData = await messagesService?.getByDealId(dealId);
      setMessages(messagesData || []);

      // Subscribe to new messages
      const msgChannel = messagesService?.subscribeToMessages(dealId, (newMessage) => {
        setMessages((prev) => [...prev, newMessage]);
      });
      setMessageChannel(msgChannel);

      // Subscribe to deal status updates
      const dlChannel = dealsService?.subscribeToDealUpdates(dealId, (updatedDeal) => {
        setDeal((prev) => ({ ...prev, ...updatedDeal }));
      });
      setDealChannel(dlChannel);

      // Subscribe to typing indicators
      const typChannel = messagesService?.subscribeToTypingIndicator(
        dealId,
        user?.id,
        (isTyping, userId) => {
          setIsTyping(isTyping);
          setTypingUserId(userId);
          if (isTyping) {
            setTimeout(() => {
              setIsTyping(false);
              setTypingUserId(null);
            }, 3000);
          }
        }
      );
      setTypingChannel(typChannel);

      // Subscribe to notifications
      if (user?.id) {
        const notifChannel = notificationsService?.subscribeToNotifications(
          user?.id,
          (newNotification) => {
            setNotifications((prev) => [newNotification, ...prev]);
            setUnreadCount((prev) => prev + 1);
          }
        );
        setNotificationChannel(notifChannel);

        // Load existing notifications
        const existingNotifications = await notificationsService?.getByUserId(user?.id);
        setNotifications(existingNotifications || []);

        const count = await notificationsService?.getUnreadCount(user?.id);
        setUnreadCount(count || 0);
      }
    } catch (error) {
      console.error('Load deal data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const translations = {
    en: {
      dealRoom: 'Deal Room',
      backToDashboard: 'Back to Dashboard',
      timeline: 'Timeline',
      summary: 'Summary',
      typing: 'is typing...',
      online: 'Online',
      offline: 'Offline'
    },
    ko: {
      dealRoom: '거래방',
      backToDashboard: '대시보드로 돌아가기',
      timeline: '타임라인',
      summary: '요약',
      typing: '입력 중...',
      online: '온라인',
      offline: '오프라인'
    }
  };

  const t = translations?.[currentLanguage];

  const statusHistory = [
    {
      status: 'draft',
      timestamp: deal?.createdAt || new Date()?.toISOString(),
      note: 'Purchase request created'
    },
    {
      status: 'offered',
      timestamp: deal?.createdAt || new Date()?.toISOString(),
      note: 'Traveler submitted an offer'
    },
    {
      status: 'accepted',
      timestamp: deal?.createdAt || new Date()?.toISOString(),
      note: 'Buyer accepted the offer'
    },
    {
      status: 'in_transit',
      timestamp: deal?.createdAt || new Date()?.toISOString(),
      note: 'Item purchased and traveler departed'
    }
  ];

  const handleSendMessage = async (content) => {
    if (!deal?.id || !user?.id) return;

    try {
      const newMessage = {
        dealId: deal?.id,
        senderId: user?.id,
        content,
        isRead: false
      };

      await messagesService?.create(newMessage);

      // Stop typing indicator after sending
      if (typingChannel) {
        messagesService?.broadcastTyping(typingChannel, user?.id, false);
      }
    } catch (error) {
      console.error('Send message error:', error);
    }
  };

  const handleTyping = () => {
    if (!typingChannel || !user?.id) return;

    // Broadcast typing indicator
    messagesService?.broadcastTyping(typingChannel, user?.id, true);

    // Clear existing timeout
    if (typingTimeoutRef?.current) {
      clearTimeout(typingTimeoutRef?.current);
    }

    // Set timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      messagesService?.broadcastTyping(typingChannel, user?.id, false);
    }, 2000);
  };

  const handleAction = (action, data) => {
    console.log('Action:', action, data);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Deal not found</p>
          <button
            onClick={() => navigate('/buyer-dashboard')}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-lg"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-20 lg:pb-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-6 md:space-y-8">
          <ProgressBar 
            currentStatus={deal?.status} 
            statusHistory={statusHistory}
            currentLanguage={currentLanguage}
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="flex flex-col h-[calc(100vh-8rem)] lg:h-[calc(100vh-12rem)]">
              <div
                ref={chatContainerRef}
                className="flex-1 overflow-y-auto px-4 md:px-6 py-4 md:py-6 bg-card">
                
                {messages?.map((message) =>
                <ChatMessage
                  key={message?.id}
                  message={{
                    id: message?.id,
                    sender: message?.sender?.fullName || 'Unknown',
                    avatar: message?.sender?.avatarUrl || 'https://via.placeholder.com/48',
                    avatarAlt: `${message?.sender?.fullName || 'User'} profile picture`,
                    content: message?.content,
                    timestamp: new Date(message?.createdAt),
                    read: message?.isRead,
                    attachment: message?.attachmentType ? {
                      type: message?.attachmentType,
                      name: message?.attachmentName,
                      size: message?.attachmentSize
                    } : null,
                    translated: message?.translated,
                    translatedContent: message?.translatedContent
                  }}
                  isOwnMessage={message?.senderId === user?.id}
                  currentLanguage={currentLanguage} />

                )}

                {isTyping &&
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-muted flex items-center justify-center">
                      <Icon name="User" size={20} color="var(--color-muted-foreground)" />
                    </div>
                    <div className="flex items-center gap-2 px-4 py-3 bg-muted rounded-2xl">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-muted-foreground rounded-full animate-pulse" />
                        <div className="w-2 h-2 bg-muted-foreground rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                        <div className="w-2 h-2 bg-muted-foreground rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                      </div>
                      <span className="text-xs md:text-sm text-muted-foreground caption ml-2">
                        {t?.typing}
                      </span>
                    </div>
                  </div>
                }
              </div>

              <div className="border-t border-border bg-card p-4 md:p-6">
                <ChatInput
                  onSendMessage={handleSendMessage}
                  onTyping={handleTyping}
                  currentLanguage={currentLanguage} />
                
              </div>

              <ActionPanel
                dealStatus={deal?.status}
                dealId={deal?.id}
                onAction={handleAction}
                currentLanguage={currentLanguage}
              />
              
            </div>

            <div className={`${
            showSummaryPanel ? 'fixed inset-0 z-150 bg-background' : 'hidden'} lg:block lg:relative lg:z-auto overflow-y-auto`
            }>
              <div className="lg:sticky lg:top-6 space-y-4 md:space-y-6 p-4 md:p-6 lg:p-0">
                {showSummaryPanel &&
                <div className="lg:hidden flex items-center justify-between mb-4">
                    <h2 className="text-xl font-heading font-semibold text-foreground">
                      {t?.summary}
                    </h2>
                    <button
                    onClick={() => setShowSummaryPanel(false)}
                    className="p-2 hover:bg-muted rounded-lg transition-base">
                    
                      <Icon name="X" size={24} />
                    </button>
                  </div>
                }

                <StatusTimeline
                  currentStatus={deal?.status}
                  statusHistory={statusHistory}
                  currentLanguage={currentLanguage} />
                

                <DealSummary
                  deal={deal}
                  currentLanguage={currentLanguage} />
                
              </div>
            </div>
          </div>
        </div>
      </main>
      <BottomTabNavigation />
    </div>);

};

export default DealRoomPage;